/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  createFrontendModule,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';

import { CUSTOMIZABLE_HOME_PAGE_FLAG } from './HomeRoot';

/**
 * Lazily loads the home page root element. Extracted so the loader can be
 * exercised directly in unit tests.
 */
export const homePageLoader = () =>
  import('./HomeRoot').then(m => <m.HomeRoot />);

/**
 * Overrides the default `/` home page provided by
 * `@backstage/plugin-home/alpha` with the custom JLL home layout and
 * registers the `customizable-home-page-preview` feature flag.
 *
 * The module targets `pluginId: 'home'` and provides a page extension with
 * no name, which resolves to the id `page:home` — the same id as the
 * plugin's default page. Per the new frontend system override rules, an
 * extension provided by a module with the same id as the plugin's default
 * takes precedence, so this custom page wins.
 *
 * `noHeader` is set because the ported {@link HomePage} renders its own
 * `<Header>` (with the world clock), matching the legacy layout.
 */
export const homePageModule = createFrontendModule({
  pluginId: 'home',
  featureFlags: [
    {
      name: CUSTOMIZABLE_HOME_PAGE_FLAG,
      description:
        'Enables the customizable, drag-and-drop home page preview in place of the default home page.',
    },
  ],
  extensions: [
    PageBlueprint.make({
      params: {
        path: '/',
        loader: homePageLoader,
        noHeader: true,
      },
    }),
  ],
});
